import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { tierSelector } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import {
  Player,
  isEvolution as isEvolutionFunction,
  isEvolutionPlayer,
} from "@repo/shared/types";
import { useSelector } from "react-redux";

import { CARD_BASE_HEIGHT, CHIP_HEIGHT } from "./compareLayout";

const STAR = "★";

/**
 * "Best meta rating" star, pinned right of a card's meta-rating chip without
 * touching `PlayerCardWithMetaRating`: an overlay inside the card wrapper's
 * `relative` box.
 *
 * The chip is content-width (tier badge and evolution upgrade arrow both
 * change it) and centered, so there's no fixed x to anchor to. Instead the
 * overlay centers an **invisible clone** of the same chip on the same axis —
 * it lands exactly over the real one — and the star hangs off its `left-full`.
 * Hence the duplicated chip props: they only need to reproduce the real chip's
 * width, but the cheapest way to be sure of that is to feed it the same data
 * the card does (`PlayerCard.tsx`).
 *
 * Vertically the overlay anchors to the card's bottom edge (`CARD_BASE_HEIGHT
 * * scale`) rather than the wrapper's, so a role name that wraps to two lines
 * underneath can't drag the star off the chip.
 */
export const BestMetaRatingStar = ({
  player,
  scale = 1,
  tierMetaRating,
}: {
  player: Player;
  /** The card's scale — the chip itself isn't scaled, only its offset is. */
  scale?: number;
  /**
   * Rating the clone's tier is graded from (tiers are always 0-chem). Must
   * match what the real chip gets, or the clone's width drifts off it.
   */
  tierMetaRating?: number;
}) => {
  const tier = useSelector(
    tierSelector(
      tierMetaRating ?? player.metaRating,
      player.playerRoleId,
      true,
    ),
  );
  const basePlayer = isEvolutionPlayer(player) ? player.basePlayer : undefined;
  const basePlayerTierLookup = useSelector(
    tierSelector(
      basePlayer?.metaRating ?? null,
      basePlayer?.playerRoleId ?? null,
      true,
    ),
  );
  const { t } = useTranslation("compare");

  return (
    <div
      className="pointer-events-none absolute inset-x-0 flex justify-center"
      style={{ top: CARD_BASE_HEIGHT * scale, height: CHIP_HEIGHT }}
    >
      <div className="invisible relative">
        <MetaRatingChip
          metaRating={player.metaRating}
          tier={tier}
          basePlayerMetaRating={basePlayer?.metaRating}
          basePlayerTier={basePlayer ? basePlayerTierLookup : undefined}
        />
        {/* `visible` re-enables the star inside the hidden clone. */}
        <span
          className="visible absolute left-full top-1/2 ml-1.5 -translate-y-1/2 text-sm leading-none text-white"
          aria-label={t("bestMetaRating")}
        >
          {STAR}
        </span>
      </div>
    </div>
  );
};
